import { getSeoRoute } from '../seo/siteRoutes.js';
import { OPENAI_ADS_CONSENT_KEY, OPENAI_ADS_CONSENT_EVENT, OPENAI_ADS_TECHNICAL_RETENTION_MS, readOpenAIAdsPreference } from './openAIAdsConsent.js';

export const OPENAI_ADS_CONTEXT_KEY = 'printeam:openai-ads-context:v1';
export const OPENAI_ADS_REVOCATION_KEY = 'printeam:openai-ads-revocation:v1';
export const OPENAI_ADS_SUPPRESSION_KEY = 'printeam:openai-ads-suppression:v1';
export const OPENAI_ADS_CONTEXT_VERSION = 2;
const ORIGINS = new Set(['https://www.printeam.co.il', 'https://printeam.co.il']);
const contextToken = (context) => context?.version === 2 ? context.revocation_token : context?.consent_token;
export const OPENAI_ADS_SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
const REFERENCE_LIMITS = { oppref: 2048, obref: 512 };
const MAX_CONTEXT_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const ISSUED_TOKEN_PREFIX = 'printeam:openai-ads-issued-token:v1:';
const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
const CONSENT_FIELDS = ['ad_storage', 'ad_user_data', 'ad_personalization'];
const runtimes = new WeakMap();
const CONSENT_TOKEN_PATTERN = /^[0-9a-f]{32}$/;

export function isOpenAIAdsContextCaptureEnabled(value = import.meta.env?.VITE_OPENAI_ADS_CONTEXT_CAPTURE_ENABLED) {
  return value === 'true';
}

export function isOpenAIAdsEnabled(value = import.meta.env?.VITE_OPENAI_ADS_ENABLED) {
  return value === 'true';
}

// Observe existing consent commands. Regional applicability is not guessed.
export function readOpenAIAdsConsent(dataLayer) {
  const consent = Object.fromEntries(CONSENT_FIELDS.map((field) => [field, 'unknown']));
  const updated = new Set();
  const regional = new Set();
  for (const command of Array.isArray(dataLayer) ? dataLayer : []) {
    if (!command || command[0] !== 'consent' || !['default', 'update'].includes(command[1])) continue;
    const parameters = command[2];
    if (!parameters || typeof parameters !== 'object' || Array.isArray(parameters)) continue;
    for (const field of CONSENT_FIELDS) {
      const value = parameters[field];
      if (!['granted', 'denied'].includes(value)) continue;
      if (Object.hasOwn(parameters, 'region')) {
        regional.add(field);
        if (consent[field] !== 'denied') consent[field] = 'unknown';
      } else if (command[1] === 'update') {
        consent[field] = value;
        updated.add(field);
        regional.delete(field);
      } else if (!updated.has(field)) {
        consent[field] = regional.has(field) && value !== 'denied' ? 'unknown' : value;
      }
    }
  }
  return consent;
}

export function hasOpenAIAdsConsent(consent) {
  return consent?.ad_storage === 'granted' && consent?.ad_user_data === 'granted';
}

function boundedText(value, limit) {
  return typeof value === 'string' && value.length > 0 && value.length <= limit
    && !/[\u0000-\u001f\u007f]/.test(value);
}

function rawValue(input, name, separator) {
  const values = String(input || '').replace(/^\?/, '').split(separator).map((part) => part.trimStart())
    .filter((part) => part.slice(0, part.indexOf('=')) === name)
    .map((part) => part.slice(part.indexOf('=') + 1));
  const limit = REFERENCE_LIMITS[name.replace(/^__/, '')];
  return values.length === 1 && boundedText(values[0], limit) && values[0] === values[0].trim() ? values[0] : '';
}

// Opaque references are never decoded, trimmed, generated, or logged.
export function readRawOpenAIAdsCookie(cookie, name) {
  return ['__oppref', '__obref'].includes(name) ? rawValue(cookie, name, ';') : '';
}

export function sanitizeOpenAIAdsSourceUrl(href) {
  try {
    const url = new URL(href);
    if (!ORIGINS.has(url.origin) || url.username || url.password) return '';
    const path = getSeoRoute(url.pathname)?.path;
    return path ? `${url.origin}${path}` : '';
  } catch (_) { return ''; }
}

export function sanitizeOpenAIAdsContext(input, consent, { now = Date.now(), sourceUrl } = {}) {
  if (!input || ![1, 2].includes(input.version)
    || consent?.ad_storage === 'denied' || consent?.ad_user_data === 'denied'
    || input.consent?.ad_storage === 'denied' || input.consent?.ad_user_data === 'denied') return undefined;
  if (input.version === 1 && !hasOpenAIAdsConsent(consent)) return undefined;
  if (!CONSENT_TOKEN_PATTERN.test(contextToken(input))) return undefined;
  const capturedMs = Date.parse(input.captured_at);
  if (!Number.isFinite(capturedMs) || capturedMs > now + 5 * 60 * 1000 || now - capturedMs >= MAX_CONTEXT_AGE_MS) return undefined;
  const source_url = sanitizeOpenAIAdsSourceUrl(sourceUrl || input.source_url);
  if (!source_url) return undefined;
  const output = {
    version: input.version, captured_at: input.captured_at, source_url,
    ...(input.version === 2 ? { revocation_token: input.revocation_token }
      : { consent: { ad_storage: 'granted', ad_user_data: 'granted' }, consent_token: input.consent_token }),
  };
  for (const key of input.version === 2 ? ['oppref'] : ['oppref', 'obref']) {
    if (boundedText(input[key], REFERENCE_LIMITS[key]) && input[key] === input[key].trim()) output[key] = input[key];
  }
  if (!output.oppref && !output.obref) return undefined;
  for (const key of UTM_FIELDS) {
    if (boundedText(input[key], 256)) output[key] = input[key];
  }
  return output;
}

function readStoredContext(storage) {
  try { return JSON.parse(storage?.getItem(OPENAI_ADS_CONTEXT_KEY) || 'null'); } catch (_) { return null; }
}

export function createOpenAIAdsRuntime({
  browser,
  enabled = isOpenAIAdsEnabled(),
  contextEnabled = isOpenAIAdsContextCaptureEnabled(),
  pixelId = import.meta.env?.VITE_OPENAI_ADS_PIXEL_ID || '',
  now = Date.now,
} = {}) {
  let initialized = false;
  let lastConsent;
  let ownedQueue;
  let forceDenied = false;
  let pendingRevocations = new Map();
  const unpersistedRevocations = new Map();
  let revocationInFlight = false;
  let lastRevocationAttempt = -Infinity;
  let approvedIncoming;
  let checkingIncoming;
  let suppressionSnapshot;
  let storageFailed = false;
  const lastEvents = new Map();
  const onceEvents = new Set();
  const storage = () => { try { return browser?.localStorage; } catch (_) { return null; } };
  const preference = () => readOpenAIAdsPreference(browser, now());
  const consent = () => {
    const global = readOpenAIAdsConsent(browser?.dataLayer);
    const selected = preference();
    const denied = forceDenied || selected?.choice === 'denied'
      || global.ad_storage === 'denied' || global.ad_user_data === 'denied';
    const state = denied ? 'denied' : selected?.choice === 'granted' ? 'granted' : 'unknown';
    return { ad_storage: state, ad_user_data: state, ad_personalization: 'denied' };
  };
  // Server-only attribution is a technical context, never an inferred visitor grant.
  const captureAllowed = () => enabled ? hasOpenAIAdsConsent(consent()) : consent().ad_storage !== 'denied';
  function readJson(key) {
    try { return JSON.parse(storage()?.getItem(key) || 'null'); } catch (_) { return null; }
  }
  function writeJson(key, value) {
    try {
      const serialized = JSON.stringify(value);
      storage()?.setItem(key, serialized);
      return storage()?.getItem(key) === serialized;
    } catch (_) { return false; }
  }
  function remove(key) { try { storage()?.removeItem(key); } catch (_) {} }
  function notify() { browser?.dispatchEvent?.(new Event(OPENAI_ADS_CONSENT_EVENT)); }

  function readEntries(prefix) {
    try {
      const target = storage();
      if (!target || typeof target.key !== 'function' || !Number.isInteger(target.length)) return null;
      const keys = Array.from({ length: target.length }, (_, index) => target.key(index)).filter((key) => key?.startsWith(prefix));
      return keys.sort().map((key) => [key, readJson(key)]);
    } catch (_) { return null; }
  }
  function readSuppression() {
    const entries = readEntries(`${OPENAI_ADS_SUPPRESSION_KEY}:`);
    if (!entries) return { generation: 'storage-unavailable', hashes: null };
    // Older pre-activation builds used one record. Preserve it conservatively until its own expiry.
    const legacy = readJson(OPENAI_ADS_SUPPRESSION_KEY);
    if (legacy) entries.push([OPENAI_ADS_SUPPRESSION_KEY, legacy]);
    if (suppressionSnapshot && !entries.some(([key]) => key === suppressionSnapshot.key)) {
      entries.push([suppressionSnapshot.key, suppressionSnapshot.record]);
    }
    const active = entries.filter(([key, value]) => {
      if (Number.isFinite(value?.created_at) && now() - value.created_at >= OPENAI_ADS_TECHNICAL_RETENTION_MS) {
        remove(key);
        if (suppressionSnapshot?.key === key) suppressionSnapshot = undefined;
        return false;
      }
      return true;
    });
    if (!active.length) return null;
    const complete = active.length <= 64 && active.every(([, value]) => Array.isArray(value?.hashes));
    const hashes = complete ? [...new Set(active.flatMap(([, value]) => value.hashes))] : null;
    return {
      generation: JSON.stringify(active.map(([key, value]) => [key, value?.hashes])),
      hashes: hashes?.length <= 64 ? hashes : null,
      pending_generations: active.filter(([, value]) => !Array.isArray(value?.hashes)).map(([key]) => key),
    };
  }
  async function fingerprint(value) {
    const digest = await browser.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  function suppressPreviousReferences(stored, includeCurrent = true) {
    approvedIncoming = undefined;
    checkingIncoming = undefined;
    const refs = [...new Set([stored?.oppref, includeCurrent ? rawValue(browser?.location?.search, 'oppref', '&') : ''].filter(Boolean))];
    const generation = newConsentToken();
    if (!generation) { forceDenied = true; return; }
    // Each tab owns only this unique entry. No read/modify/write of another tab's pending work.
    const key = `${OPENAI_ADS_SUPPRESSION_KEY}:${generation}`;
    const record = { version: 1, created_at: now(), hashes: null };
    suppressionSnapshot = { key, record };
    writeJson(key, record);
    Promise.all(refs.map(fingerprint)).then((hashes) => {
      const completed = { ...record, hashes };
      if (writeJson(key, completed) && suppressionSnapshot?.key === key) suppressionSnapshot = undefined;
    }).catch(() => {});
  }
  function referenceApprovalKey(incoming, suppression) {
    return JSON.stringify([incoming, preference()?.consent_token, suppression.generation,
      suppression.hashes, suppression.pending_hashes, suppression.pending_generations, suppression.allowed_consent_token]);
  }

  function suppressionAllowsCurrentReference() {
    const suppression = readSuppression();
    if (!suppression) return true;
    const incoming = rawValue(browser?.location?.search, 'oppref', '&');
    const saved = sanitizeOpenAIAdsContext(readStoredContext(storage()), consent(), { now: now() });
    if (Array.isArray(suppression.hashes) && saved
      && (saved.version === 2 ? issuedTokens().includes(saved.revocation_token) : saved.consent_token === preference()?.consent_token)
      && saved?.oppref && (!incoming || incoming === saved.oppref)) return true;
    if (!incoming || !Array.isArray(suppression.hashes)) return false;
    const approvalKey = referenceApprovalKey(incoming, suppression);
    if (approvedIncoming === approvalKey) return true;
    if (checkingIncoming !== approvalKey && captureAllowed()) {
      checkingIncoming = approvalKey;
      fingerprint(incoming).then((hash) => {
        const latest = readSuppression();
        if (checkingIncoming !== approvalKey || !Array.isArray(latest?.hashes)
          || referenceApprovalKey(incoming, latest) !== approvalKey
          || latest.hashes.includes(hash) || !captureAllowed()) return;
        approvedIncoming = approvalKey;
        if (rawValue(browser?.location?.search, 'oppref', '&') === incoming) getContext();
      }).catch(() => {});
    }
    return false;
  }
  function newConsentToken() {
    try {
      const bytes = browser?.crypto?.getRandomValues(new Uint8Array(16));
      return bytes ? Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('') : undefined;
    } catch (_) { return undefined; }
  }
  function loadRevocations() {
    const entries = readEntries(`${OPENAI_ADS_REVOCATION_KEY}:`);
    if (!entries) return;
    pendingRevocations = new Map(unpersistedRevocations);
    for (const [key, item] of entries) {
      const token = key.slice(OPENAI_ADS_REVOCATION_KEY.length + 1);
      if (!CONSENT_TOKEN_PATTERN.test(token) || !Number.isFinite(item?.created_at)) continue;
      if (now() - item.created_at >= OPENAI_ADS_TECHNICAL_RETENTION_MS) { remove(key); continue; }
      pendingRevocations.set(token, item.created_at);
    }
  }
  function queueRevocation(token) {
    if (!CONSENT_TOKEN_PATTERN.test(token)) return;
    const key = `${OPENAI_ADS_REVOCATION_KEY}:${token}`;
    const saved = readJson(key);
    const created_at = Number.isFinite(saved?.created_at) ? saved.created_at : now();
    // A token owns its own deletion capability; simultaneous withdrawals cannot overwrite it.
    if (!writeJson(key, { version: 1, created_at })) unpersistedRevocations.set(token, created_at);
    else unpersistedRevocations.delete(token);
    pendingRevocations.set(token, created_at);
  }
  function issuedTokens() {
    const entries = readEntries(ISSUED_TOKEN_PREFIX) || [];
    return entries.flatMap(([key, item]) => {
      if (!Number.isFinite(item?.created_at) || now() - item.created_at >= OPENAI_ADS_TECHNICAL_RETENTION_MS) { remove(key); return []; }
      const token = key.slice(ISSUED_TOKEN_PREFIX.length);
      return CONSENT_TOKEN_PATTERN.test(token) ? [token] : [];
    });
  }
  function queueAllIssuedTokens() { for (const token of issuedTokens()) queueRevocation(token); }
  function retryRevocation() {
    loadRevocations();
    if (!pendingRevocations.size || revocationInFlight || typeof browser?.fetch !== 'function'
      || now() - lastRevocationAttempt < 30000) return;
    // Withdrawal works even after collection flags are disabled.
    const tokens = [...pendingRevocations.keys()];
    revocationInFlight = true;
    lastRevocationAttempt = now();
    Promise.all(tokens.map(async (token) => {
      try {
        const response = await browser.fetch('/api/openai-ads/revoke', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          mode: 'same-origin', credentials: 'same-origin', keepalive: true,
          body: JSON.stringify({ version: 2, revocation_token: token }),
        });
        if (response?.ok) {
          remove(`${OPENAI_ADS_REVOCATION_KEY}:${token}`);
          remove(`${ISSUED_TOKEN_PREFIX}${token}`);
          pendingRevocations.delete(token);
          unpersistedRevocations.delete(token);
        }
      } catch (_) {}
    })).finally(() => { revocationInFlight = false; notify(); });
  }
  function expireStoredContext() {
    const saved = readStoredContext(storage());
    if (saved && (!Number.isFinite(Date.parse(saved.captured_at))
      || now() - Date.parse(saved.captured_at) >= MAX_CONTEXT_AGE_MS)) {
      suppressPreviousReferences(saved, false);
      remove(OPENAI_ADS_CONTEXT_KEY);
    }
  }
  function purgeLegacySessionContext() {
    // Previous releases used session storage. Never import their attribution or consent.
    try {
      const legacy = browser?.sessionStorage;
      if (!legacy || legacy === storage()) return;
      const saved = readStoredContext(legacy);
      const revocation = legacy.getItem(OPENAI_ADS_REVOCATION_KEY);
      if (saved) { queueRevocation(contextToken(saved)); suppressPreviousReferences(saved); }
      if (CONSENT_TOKEN_PATTERN.test(revocation)) queueRevocation(revocation);
      for (const key of [OPENAI_ADS_CONTEXT_KEY, OPENAI_ADS_REVOCATION_KEY, OPENAI_ADS_SUPPRESSION_KEY]) legacy.removeItem(key);
    } catch (_) {}
  }
  function syncConsent() {
    try {
      purgeLegacySessionContext();
      readSuppression();
      expireStoredContext();
      issuedTokens();
      const selected = preference();
      const expired = readJson(OPENAI_ADS_CONSENT_KEY);
      if (expired && !selected) {
        queueRevocation(expired.consent_token);
        if (expired.choice === 'granted') suppressPreviousReferences(readStoredContext(storage()));
        readSuppression();
        remove(OPENAI_ADS_CONSENT_KEY);
        remove(OPENAI_ADS_CONTEXT_KEY);
      }
      const current = consent();
      const allowed = hasOpenAIAdsConsent(current);
      if (!captureAllowed()) {
        queueAllIssuedTokens();
        const stored = readStoredContext(storage());
        if (stored || (selected?.choice === 'granted' && current.ad_storage === 'denied')) {
          suppressPreviousReferences(stored);
          queueRevocation(selected?.consent_token);
          queueRevocation(contextToken(stored));
          if (selected?.choice !== 'denied') writeJson(OPENAI_ADS_CONSENT_KEY, { version: 1, choice: 'denied', updated_at: now(), suppression_required: true });
        }
        remove(OPENAI_ADS_CONTEXT_KEY);
        if (ownedQueue && browser?.oaiq === ownedQueue) {
          ownedQueue.q = ownedQueue.q.filter((args) => args[0] !== 'measure' && !(args[0] === 'init' && args[1]?.user));
        }
      }
      const sdkAllowed = enabled && allowed && suppressionAllowsCurrentReference();
      if (initialized && lastConsent !== sdkAllowed) browser.oaiq('consent', sdkAllowed);
      lastConsent = sdkAllowed;
      retryRevocation();
      return { current, allowed };
    } catch (_) { return { current: {}, allowed: false }; }
  }
  function setConsent(choice) {
    if (!['granted', 'denied'].includes(choice)) return false;
    const previous = preference();
    if (choice === 'granted' && previous?.suppression_required && !readSuppression()) return false;
    if (choice === 'granted' && suppressionSnapshot && !readJson(suppressionSnapshot.key)) return false;
    if (choice === 'granted' && previous?.choice === 'denied') queueAllIssuedTokens();
    const stored = readStoredContext(storage());
    if (choice === 'denied') {
      forceDenied = true;
      queueAllIssuedTokens();
      suppressPreviousReferences(stored);
      queueRevocation(previous?.consent_token);
      queueRevocation(contextToken(stored));
      remove(OPENAI_ADS_CONTEXT_KEY);
    }
    if (choice === 'granted' && !contextEnabled) return false;
    const token = choice === 'granted' ? previous?.choice === 'granted' ? previous.consent_token : newConsentToken() : undefined;
    if (choice === 'granted' && !token) return false;
    // Independently record every issued capability before exposing its grant, including simultaneous first grants.
    if (token) {
      const key = `${ISSUED_TOKEN_PREFIX}${token}`;
      const saved = readJson(key);
      if (issuedTokens().length >= 64 || !writeJson(key, { version: 1, created_at: saved?.created_at ?? now() })) { storageFailed = true; return false; }
    }
    const persisted = writeJson(OPENAI_ADS_CONSENT_KEY, {
      version: 1, choice, updated_at: now(), ...(token ? { consent_token: token } : {}),
      ...(choice === 'denied' || previous?.suppression_required ? { suppression_required: true } : {}),
    });
    storageFailed = !persisted;
    if (persisted) forceDenied = false;
    else if (choice === 'denied') remove(OPENAI_ADS_CONSENT_KEY);
    syncConsent();
    // Capture the actual current click on grant without synthesizing browser events.
    if (persisted && choice === 'granted') getContext();
    notify();
    return persisted;
  }
  function getConsentState() {
    const selected = preference();
    const current = consent();
    return {
      choice: selected?.choice || 'unknown', effective: current.ad_storage,
      contextEnabled, storageFailed, revocationPending: pendingRevocations.size > 0,
    };
  }
  function getContext() {
    try {
      const { current } = syncConsent();
      if (!captureAllowed()) return current.ad_storage === 'denied'
        ? { version: 1, consent: { ad_storage: 'denied', ad_user_data: 'denied' } } : undefined;
      if (!contextEnabled || !browser || pendingRevocations.size || !suppressionAllowsCurrentReference()) return undefined;
      const suppressionGeneration = readSuppression()?.generation;
      const source_url = sanitizeOpenAIAdsSourceUrl(browser.location?.href);
      if (!source_url) return undefined;
      const search = browser.location?.search || '';
      const incoming = rawValue(search, 'oppref', '&');
      const grantToken = preference()?.consent_token;
      const stored = sanitizeOpenAIAdsContext(readStoredContext(storage()), current, { now: now(), sourceUrl: source_url });
      const previous = stored?.version === 2 ? stored : stored?.consent_token === grantToken ? stored : undefined;
      // Server-only measurement accepts URL click references, never Pixel cookies or user identifiers.
      let captured = previous?.oppref ? { ...previous } : undefined;
      if (incoming && incoming !== previous?.oppref) {
        const version = enabled ? 1 : OPENAI_ADS_CONTEXT_VERSION;
        const token = version === 1 ? grantToken : newConsentToken();
        if (!token) return undefined;
        if (version === 2 && (issuedTokens().length >= 64
          || !writeJson(`${ISSUED_TOKEN_PREFIX}${token}`, { version: 2, created_at: now() }))) {
          storageFailed = true; return undefined;
        }
        captured = { version, captured_at: new Date(now()).toISOString(), source_url, oppref: incoming,
          ...(version === 2 ? { revocation_token: token }
            : { consent: { ad_storage: 'granted', ad_user_data: 'granted' }, consent_token: token }) };
        const parameters = new URLSearchParams(search);
        for (const key of UTM_FIELDS) {
          const values = parameters.getAll(key);
          if (values.length === 1 && boundedText(values[0], 256)) captured[key] = values[0];
        }
      }
      if (captured) delete captured.obref;
      // Another tab can withdraw while this tab is recording its new capability.
      // Re-read after issuance and again after publishing so withdrawal either
      // observes this token/context or this tab queues its own revocation.
      const canPublish = () => {
        syncConsent();
        if (readSuppression()?.generation !== suppressionGeneration) {
          // A withdrawal generation invalidates the in-progress snapshot even
          // when a later explicit choice has already cleared the denial.
          queueRevocation(contextToken(captured));
          if (contextToken(readStoredContext(storage())) === contextToken(captured)) remove(OPENAI_ADS_CONTEXT_KEY);
          retryRevocation();
          return false;
        }
        return captureAllowed() && !pendingRevocations.size && suppressionAllowsCurrentReference();
      };
      if (!canPublish()) return consent().ad_storage === 'denied'
        ? { version: 1, consent: { ad_storage: 'denied', ad_user_data: 'denied' } } : undefined;
      const result = sanitizeOpenAIAdsContext(captured, consent(), { now: now(), sourceUrl: source_url });
      if (!result || !writeJson(OPENAI_ADS_CONTEXT_KEY, result)) return undefined;
      if (!canPublish()) return consent().ad_storage === 'denied'
        ? { version: 1, consent: { ad_storage: 'denied', ad_user_data: 'denied' } } : undefined;
      return result;
    } catch (_) { return undefined; }
  }
  function initialize() {
    if (!enabled || !syncConsent().allowed || !suppressionAllowsCurrentReference()
      || !boundedText(pixelId, 256) || !browser?.document) return false;
    if (initialized) return true;
    try {
      if (typeof browser.oaiq !== 'function') {
        const queue = function () { queue.q.push(arguments); };
        queue.q = [];
        browser.oaiq = queue;
        ownedQueue = queue;
        const script = browser.document.createElement('script');
        script.async = true;
        script.src = OPENAI_ADS_SDK_URL;
        browser.document.head.appendChild(script);
      }
      browser.oaiq('consent', true);
      browser.oaiq('init', { pixelId });
      initialized = true;
      lastConsent = true;
      return true;
    } catch (_) { return false; }
  }
  function measure(event, data, { key, once = false } = {}) {
    try {
      if (!['page_viewed', 'contents_viewed', 'items_added', 'checkout_started'].includes(event)) return false;
      getContext();
      if (!initialize()) return false;
      if (key && (once ? onceEvents.has(`${event}:${key}`) : lastEvents.get(event) === key)) return false;
      browser.oaiq('measure', event, data, { opt_out: true });
      if (key) {
        if (once) onceEvents.add(`${event}:${key}`);
        else lastEvents.set(event, key);
      }
      return true;
    } catch (_) { return false; }
  }
  function observeConsent() {
    if (!browser) return () => {};
    syncConsent();
    const layer = browser.dataLayer;
    const originalPush = layer?.push;
    let wrapper;
    if (Array.isArray(layer) && typeof originalPush === 'function') {
      wrapper = function (...args) { const result = originalPush.apply(this, args); syncConsent(); notify(); return result; };
      try { layer.push = wrapper; } catch (_) { wrapper = undefined; }
    }
    const listener = () => { syncConsent(); notify(); };
    for (const event of ['focus', 'storage', 'online']) browser.addEventListener?.(event, listener);
    // Enforce expiry while a tab remains open; timers are cleaned up on unmount.
    const interval = browser.setInterval?.(listener, 60000);
    return () => {
      if (wrapper && layer.push === wrapper) layer.push = originalPush;
      for (const event of ['focus', 'storage', 'online']) browser.removeEventListener?.(event, listener);
      if (interval !== undefined) browser.clearInterval?.(interval);
    };
  }
  return { getContext, measure, syncConsent, observeConsent, setConsent, getConsentState };
}

export function getOpenAIAdsRuntime(browser = typeof window !== 'undefined' ? window : null) {
  if (!browser) return null;
  if (!runtimes.has(browser)) runtimes.set(browser, createOpenAIAdsRuntime({ browser }));
  return runtimes.get(browser);
}

export function getOpenAIAdsContext() {
  try { return getOpenAIAdsRuntime()?.getContext(); } catch (_) { return undefined; }
}

// Revalidate delayed handoffs against current consent rather than replaying a grant.
export function gateOpenAIAdsHandoff(context, consent) {
  if (!context) return undefined;
  if (consent?.ad_storage === 'denied' || consent?.ad_user_data === 'denied') {
    return { version: 1, consent: { ad_storage: 'denied', ad_user_data: 'denied' } };
  }
  if (context.version === 1 && context.consent?.ad_storage === 'denied'
    && context.consent?.ad_user_data === 'denied') return context;
  return sanitizeOpenAIAdsContext(context, consent);
}
